import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { del, patch, post } from "common/helpers/HTTP";

import type { ApiToken } from "../types";

async function createApiTokenRequest(
  workspaceId: string,
  name: string,
  write: boolean,
): Promise<ApiToken> {
  return (
    await post(`workspaces/${workspaceId}/tokens`, {
      name: name.trim(),
      write,
    })
  ).token;
}

async function deleteApiTokenRequest(
  workspaceId: string,
  tokenId: string,
): Promise<void> {
  await del(`workspaces/${workspaceId}/tokens/${tokenId}`);
}

async function updateApiTokenRequest(
  workspaceId: string,
  tokenId: string,
  updates: Partial<{ name: string; write: boolean }>,
): Promise<ApiToken> {
  return (await patch(`workspaces/${workspaceId}/tokens/${tokenId}`, updates))
    .token;
}

export default function useApiTokensMutations() {
  const queryClient = useQueryClient();
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const { mutateAsync: createApiToken, isPending: isCreatingToken } =
    useMutation({
      mutationFn: ({ name, write }: { name: string; write: boolean }) =>
        createApiTokenRequest(workspaceId, name, write),
      onSuccess: (newToken) => {
        queryClient.setQueryData<ApiToken[]>(["apiTokens"], (prevTokens = []) =>
          [...prevTokens, newToken].sort((a, b) => a.created_at - b.created_at),
        );

        toast.success("API key created");
      },
      onError: () => {
        toast.error("Failed to create API key");
      },
    });

  const { mutateAsync: deleteApiToken, isPending: isDeletingToken } =
    useMutation({
      mutationFn: (tokenId: string) =>
        deleteApiTokenRequest(workspaceId, tokenId),
      onSuccess: (_, tokenId) => {
        queryClient.setQueryData<ApiToken[]>(["apiTokens"], (prevTokens = []) =>
          prevTokens.filter((token) => token.id !== tokenId),
        );

        toast.success("API key deleted");
      },
      onError: () => {
        toast.error("Failed to delete API key");
      },
    });

  const { mutateAsync: updateApiToken, isPending: isUpdatingToken } =
    useMutation({
      mutationFn: ({
        tokenId,
        updates,
      }: {
        tokenId: string;
        updates: Partial<{ name: string; write: boolean }>;
      }) => updateApiTokenRequest(workspaceId, tokenId, updates),
      onSuccess: (updatedToken) => {
        queryClient.setQueryData<ApiToken[]>(["apiTokens"], (prevTokens = []) =>
          prevTokens
            .map((token) =>
              token.id === updatedToken.id ? updatedToken : token,
            )
            .sort((a, b) => a.created_at - b.created_at),
        );
      },
      onError: () => {
        toast.error("Failed to update API token");
      },
    });

  return {
    createApiToken,
    isCreatingToken,
    deleteApiToken,
    isDeletingToken,
    updateApiToken,
    isUpdatingToken,
  };
}
